import type { SourceConnection, UploadKind, UploadSession } from "@webyonetim/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileArchive, GitBranch, GitFork, Pause, Play, ShieldCheck, Upload, X } from "lucide-react";
import { useRef, useState, type ChangeEvent, type ReactNode, type SyntheticEvent } from "react";

import { Button, PasswordField, SelectField, TextField } from "../../components/ui/Controls";
import { ErrorState, InlineResult, Skeleton, StatusBadge } from "../../components/ui/Feedback";
import { apiErrorMessage } from "../../lib/auth";
import { sourcesApi, uploadsApi, type SourceConnectionMutationResponse } from "../../lib/api";
import { uploadFileInParts, type UploadProgress } from "./resumable-upload";

type SourceMethod = "github" | "gitlab" | "generic_git" | "zip";

export interface SourceSetupProps {
  readonly projectId: string;
  readonly onConnected?: (connection: SourceConnection) => void;
  readonly onUploadAccepted?: (jobId: string) => void;
}

const sourceMethods: readonly {
  readonly code: SourceMethod;
  readonly description: string;
  readonly Icon: typeof GitFork;
  readonly label: string;
}[] = [
  {
    code: "github",
    description: "GitHub App ile yalnız seçtiğiniz repository’ye erişin.",
    Icon: GitFork,
    label: "GitHub",
  },
  {
    code: "gitlab",
    description: "GitLab.com veya kendi GitLab sunucunuzu bağlayın.",
    Icon: GitBranch,
    label: "GitLab",
  },
  {
    code: "generic_git",
    description: "Herhangi bir HTTPS ya da SSH Git repository’si kullanın.",
    Icon: GitBranch,
    label: "Genel Git",
  },
  {
    code: "zip",
    description: "Kaynak kodu veya hazır statik çıktıyı güvenli ZIP olarak yükleyin.",
    Icon: FileArchive,
    label: "ZIP / Hazır paket",
  },
];

export function SourceSetup({ onConnected, onUploadAccepted, projectId }: SourceSetupProps) {
  const [method, setMethod] = useState<SourceMethod>("github");
  const queryClient = useQueryClient();
  const connections = useQuery({
    queryKey: ["source-connections", projectId],
    queryFn: ({ signal }) => sourcesApi.list(projectId, signal),
    retry: false,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["source-connections", projectId] });
  };
  const connected = (response: SourceConnectionMutationResponse) => {
    refresh();
    onConnected?.(response.connection);
  };

  return (
    <div className="stack">
      <section className="card stack">
        <div>
          <p className="eyebrow">Kaynak girişi</p>
          <h2>Uygulamanız nerede?</h2>
          <p>
            Git kimlik bilgileri geri okunamaz; ZIP dosyaları yayınlanmadan önce karantinada açılır,
            limitlenir ve zararlı yazılım taramasından geçer.
          </p>
        </div>
        <fieldset className="source-method-grid">
          <legend className="visually-hidden">Kaynak yöntemi</legend>
          {sourceMethods.map(({ code, description, Icon, label }) => (
            <label className="source-method" key={code}>
              <input
                checked={method === code}
                name="source-method"
                onChange={() => {
                  setMethod(code);
                }}
                type="radio"
                value={code}
              />
              <Icon aria-hidden="true" size={24} strokeWidth={1.6} />
              <strong>{label}</strong>
              <span>{description}</span>
            </label>
          ))}
        </fieldset>
        {method === "github" ? <GithubSetup projectId={projectId} /> : null}
        {method === "gitlab" ? <GitlabSetup onConnected={connected} projectId={projectId} /> : null}
        {method === "generic_git" ? (
          <GenericGitSetup onConnected={connected} projectId={projectId} />
        ) : null}
        {method === "zip" ? (
          <ZipUpload onAccepted={onUploadAccepted} projectId={projectId} />
        ) : null}
      </section>

      <section className="card stack">
        <div className="split-row">
          <div>
            <h2>Bağlı Git kaynakları</h2>
            <p className="muted">Bağlantı testi gerçek source agent üzerinden imzalı çalışır.</p>
          </div>
          <Button
            onClick={() => {
              refresh();
            }}
            variant="secondary"
          >
            Yenile
          </Button>
        </div>
        {connections.isLoading ? <Skeleton /> : null}
        {connections.isError ? <ErrorState onRetry={refresh} /> : null}
        {connections.data?.items.length === 0 ? (
          <p className="muted">Henüz bir Git kaynağı bağlı değil.</p>
        ) : null}
        {connections.data?.items.map((connection) => (
          <ConnectionRow connection={connection} key={connection.id} projectId={projectId} />
        ))}
      </section>
    </div>
  );
}

function GithubSetup({ projectId }: { readonly projectId: string }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const start = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const response = await sourcesApi.startGithub(projectId);
      const installationUrl = new URL(response.installationUrl);
      if (
        installationUrl.protocol !== "https:" ||
        installationUrl.hostname !== "github.com" ||
        !installationUrl.pathname.startsWith("/apps/")
      ) {
        throw new Error("Sunucu güvenli bir GitHub App kurulum adresi döndürmedi.");
      }
      window.location.assign(installationUrl.href);
    } catch (caught) {
      setError(apiErrorMessage(caught));
      setBusy(false);
    }
  };
  return (
    <div className="form-stack">
      <p>
        GitHub App kurulumu sizi GitHub’a yönlendirir. Kişisel erişim tokenı istemez; yalnız
        seçtiğiniz kurulum ve repository kullanılabilir.
      </p>
      {error === undefined ? null : <InlineResult tone="error">{error}</InlineResult>}
      <Button
        busy={busy}
        busyLabel="GitHub açılıyor…"
        onClick={() => {
          void start();
        }}
      >
        <GitFork aria-hidden="true" size={18} /> GitHub’a bağlan
      </Button>
    </div>
  );
}

function GitlabSetup({
  onConnected,
  projectId,
}: {
  readonly onConnected: (response: SourceConnectionMutationResponse) => void;
  readonly projectId: string;
}) {
  const [baseUrl, setBaseUrl] = useState("https://gitlab.com");
  const [projectPath, setProjectPath] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<SourceConnectionMutationResponse>();
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setResult(undefined);
    try {
      const response = await sourcesApi.create(projectId, {
        provider: "gitlab",
        baseUrl,
        projectPath,
        accessToken,
        webhookSecret,
      });
      setResult(response);
      onConnected(response);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setAccessToken("");
      setWebhookSecret("");
      setBusy(false);
    }
  };
  return (
    <form
      className="form-stack"
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <TextField
        label="GitLab adresi"
        onChange={(event) => {
          setBaseUrl(event.currentTarget.value);
        }}
        required
        type="url"
        value={baseUrl}
      />
      <TextField
        help="Örnek: ekip/uygulama"
        label="Proje yolu"
        onChange={(event) => {
          setProjectPath(event.currentTarget.value);
        }}
        pattern="[A-Za-z0-9_.-]+/[A-Za-z0-9_./-]+"
        required
        value={projectPath}
      />
      <PasswordField
        autoComplete="off"
        help="Yalnız yazılır, panelden tekrar görüntülenemez."
        label="Erişim tokenı"
        minLength={8}
        onChange={(event) => {
          setAccessToken(event.currentTarget.value);
        }}
        required
        value={accessToken}
      />
      <PasswordField
        autoComplete="new-password"
        help="GitLab webhook doğrulaması için ayrı, rastgele bir sır kullanın."
        label="Webhook sırrı"
        minLength={16}
        onChange={(event) => {
          setWebhookSecret(event.currentTarget.value);
        }}
        required
        value={webhookSecret}
      />
      <MutationFeedback error={error} result={result} />
      <Button busy={busy} busyLabel="Bağlantı ekleniyor…" type="submit">
        GitLab kaynağını ekle
      </Button>
    </form>
  );
}

function GenericGitSetup({
  onConnected,
  projectId,
}: {
  readonly onConnected: (response: SourceConnectionMutationResponse) => void;
  readonly projectId: string;
}) {
  const [protocol, setProtocol] = useState<"https" | "ssh">("https");
  const [repositoryUrl, setRepositoryUrl] = useState("");
  const [useCredential, setUseCredential] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [privateKey, setPrivateKey] = useState("");
  const [knownHost, setKnownHost] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [result, setResult] = useState<SourceConnectionMutationResponse>();
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setResult(undefined);
    try {
      const response = await sourcesApi.create(
        projectId,
        protocol === "ssh"
          ? {
              provider: "generic_git",
              repositoryUrl,
              credential: { kind: "ssh_key", privateKey, knownHost },
            }
          : {
              provider: "generic_git",
              repositoryUrl,
              ...(useCredential
                ? { credential: { kind: "https_basic" as const, username, password } }
                : {}),
            },
      );
      setResult(response);
      onConnected(response);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setPassword("");
      setPrivateKey("");
      setKnownHost("");
      setBusy(false);
    }
  };
  return (
    <form
      className="form-stack"
      onSubmit={(event) => {
        void submit(event);
      }}
    >
      <SelectField
        label="Bağlantı türü"
        onChange={(event) => {
          const next = event.currentTarget.value as "https" | "ssh";
          setProtocol(next);
          setRepositoryUrl(next === "ssh" ? "ssh://git@" : "https://");
        }}
        value={protocol}
      >
        <option value="https">HTTPS</option>
        <option value="ssh">SSH</option>
      </SelectField>
      <TextField
        help="URL içinde kullanıcı adı, parola veya token bulunamaz."
        label="Repository URL"
        onChange={(event) => {
          setRepositoryUrl(event.currentTarget.value);
        }}
        pattern={protocol === "ssh" ? "ssh://.*" : "https://.*"}
        required
        type="url"
        value={repositoryUrl}
      />
      {protocol === "https" ? (
        <>
          <label className="cluster">
            <input
              checked={useCredential}
              onChange={(event) => {
                setUseCredential(event.currentTarget.checked);
              }}
              type="checkbox"
            />
            Private repository için kimlik bilgisi kullan
          </label>
          {useCredential ? (
            <>
              <TextField
                autoComplete="username"
                label="Kullanıcı adı"
                onChange={(event) => {
                  setUsername(event.currentTarget.value);
                }}
                required
                value={username}
              />
              <PasswordField
                autoComplete="off"
                label="Parola veya erişim tokenı"
                minLength={8}
                onChange={(event) => {
                  setPassword(event.currentTarget.value);
                }}
                required
                value={password}
              />
            </>
          ) : null}
        </>
      ) : (
        <>
          <div className="field">
            <label htmlFor="generic-private-key">Private key</label>
            <textarea
              autoComplete="off"
              id="generic-private-key"
              onChange={(event) => {
                setPrivateKey(event.currentTarget.value);
              }}
              required
              spellCheck={false}
              value={privateKey}
            />
            <p className="field__help">
              Yalnız yazılır; SSH agent veya kullanıcı config’i kullanılmaz.
            </p>
          </div>
          <div className="field">
            <label htmlFor="generic-known-host">Sabit known_hosts satırı</label>
            <textarea
              id="generic-known-host"
              onChange={(event) => {
                setKnownHost(event.currentTarget.value);
              }}
              required
              spellCheck={false}
              value={knownHost}
            />
            <p className="field__help">Tek host ve tek açık anahtar satırı gerekir.</p>
          </div>
        </>
      )}
      <MutationFeedback error={error} result={result} />
      <Button busy={busy} busyLabel="Bağlantı ekleniyor…" type="submit">
        Git kaynağını ekle
      </Button>
    </form>
  );
}

function ZipUpload({
  onAccepted,
  projectId,
}: {
  readonly onAccepted?: ((jobId: string) => void) | undefined;
  readonly projectId: string;
}) {
  const [file, setFile] = useState<File>();
  const [kind, setKind] = useState<UploadKind>("source_zip");
  const [session, setSession] = useState<UploadSession>();
  const [progress, setProgress] = useState<UploadProgress>();
  const [jobId, setJobId] = useState<string>();
  const [digest, setDigest] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [paused, setPaused] = useState(false);
  const [error, setError] = useState<string>();
  const abortRef = useRef<AbortController | undefined>(undefined);

  const run = async (currentFile: File, currentSession: UploadSession) => {
    const abort = new AbortController();
    abortRef.current = abort;
    setBusy(true);
    setPaused(false);
    setError(undefined);
    try {
      const result = await uploadFileInParts({
        file: currentFile,
        session: currentSession,
        transport: uploadsApi,
        signal: abort.signal,
        onProgress: setProgress,
      });
      setSession({ ...result.session, status: "finalizing" });
      setDigest(result.digest);
      setJobId(result.accepted.jobId);
      onAccepted?.(result.accepted.jobId);
    } catch (caught) {
      if (abort.signal.aborted) setPaused(true);
      else setError(apiErrorMessage(caught));
    } finally {
      if (abortRef.current === abort) abortRef.current = undefined;
      setBusy(false);
    }
  };

  const start = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (file === undefined) return;
    if (file.size > 512 * 1_024 * 1_024) {
      setError("ZIP dosyası 512 MiB sınırını aşıyor.");
      return;
    }
    setBusy(true);
    setError(undefined);
    setJobId(undefined);
    setDigest(undefined);
    try {
      const created = await uploadsApi.create({
        projectId,
        kind,
        filename: file.name,
        contentLength: file.size,
      });
      setSession(created);
      await run(file, created);
    } catch (caught) {
      setError(apiErrorMessage(caught));
      setBusy(false);
    }
  };

  const resume = async () => {
    if (file === undefined || session === undefined) return;
    setBusy(true);
    setError(undefined);
    try {
      const latest = await uploadsApi.get(session.id);
      setSession(latest);
      await run(file, latest);
    } catch (caught) {
      setError(apiErrorMessage(caught));
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (session === undefined) return;
    abortRef.current?.abort(new DOMException("Kullanıcı uploadı iptal etti.", "AbortError"));
    setBusy(true);
    setError(undefined);
    try {
      let latest = await uploadsApi.get(session.id);
      if (latest.status === "open") {
        try {
          latest = await uploadsApi.cancel(latest.id, latest.revision);
        } catch {
          latest = await uploadsApi.get(session.id);
          if (latest.status === "open")
            latest = await uploadsApi.cancel(latest.id, latest.revision);
        }
      }
      setSession(latest);
      setPaused(false);
    } catch (caught) {
      setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.currentTarget.files?.[0];
    setFile(selected);
    setSession(undefined);
    setProgress(undefined);
    setJobId(undefined);
    setDigest(undefined);
    setError(undefined);
    setPaused(false);
  };
  const displayedBytes = progress?.processedBytes ?? session?.receivedBytes ?? 0;
  const totalBytes = file?.size ?? session?.contentLength ?? 0;

  return (
    <form
      className="form-stack"
      onSubmit={(event) => {
        void start(event);
      }}
    >
      <SelectField
        help="Kaynak ZIP framework algılamasına gider; hazır statik çıktı çalıştırılmadan artifact olur."
        label="ZIP içeriği"
        onChange={(event) => {
          setKind(event.currentTarget.value as UploadKind);
        }}
        value={kind}
      >
        <option value="source_zip">Kaynak kod</option>
        <option value="ready_build">Hazır statik çıktı</option>
      </SelectField>
      <div className="field">
        <label htmlFor="source-zip">ZIP dosyası</label>
        <input
          accept=".zip,application/zip"
          id="source-zip"
          onChange={selectFile}
          required
          type="file"
        />
        <p className="field__help">
          En fazla 512 MiB. Symlink, şifreli arşiv, yol kaçışı ve limit aşımı kapalı reddedilir.
        </p>
      </div>
      {file === undefined ? null : (
        <dl className="definition-grid">
          <div>
            <dt>Dosya</dt>
            <dd>{file.name}</dd>
          </div>
          <div>
            <dt>Boyut</dt>
            <dd>{formatBytes(file.size)}</dd>
          </div>
        </dl>
      )}
      {progress === undefined ? null : (
        <div className="stack stack--tight" aria-live="polite">
          <div className="split-row">
            <strong>{progressLabel(progress.phase)}</strong>
            <span className="technical">
              {formatBytes(displayedBytes)} / {formatBytes(totalBytes)}
            </span>
          </div>
          <progress max={Math.max(totalBytes, 1)} value={displayedBytes} />
        </div>
      )}
      {error === undefined ? null : <InlineResult tone="error">{error}</InlineResult>}
      {jobId === undefined ? null : (
        <InlineResult tone="info">
          Tarama ve kabul işi başlatıldı. İş kimliği: <code>{jobId}</code>
          {digest === undefined ? null : (
            <>
              {" "}
              Arşiv özeti: <code>{digest}</code>
            </>
          )}
        </InlineResult>
      )}
      <div className="form-actions">
        {session === undefined ? (
          <Button
            busy={busy}
            busyLabel="Upload başlatılıyor…"
            disabled={file === undefined}
            type="submit"
          >
            <Upload aria-hidden="true" size={18} /> Güvenli uploadı başlat
          </Button>
        ) : null}
        {busy && session !== undefined ? (
          <Button
            onClick={() => {
              abortRef.current?.abort(new DOMException("Upload duraklatıldı.", "AbortError"));
            }}
            variant="secondary"
          >
            <Pause aria-hidden="true" size={18} /> Duraklat
          </Button>
        ) : null}
        {paused && session?.status === "open" ? (
          <Button
            onClick={() => {
              void resume();
            }}
          >
            <Play aria-hidden="true" size={18} /> Devam et
          </Button>
        ) : null}
        {session?.status === "open" ? (
          <Button
            busy={busy && !paused}
            busyLabel="İptal ediliyor…"
            onClick={() => {
              void cancel();
            }}
            variant="destructive"
          >
            <X aria-hidden="true" size={18} /> Uploadı iptal et
          </Button>
        ) : null}
      </div>
    </form>
  );
}

function MutationFeedback({
  error,
  result,
}: {
  readonly error: string | undefined;
  readonly result: SourceConnectionMutationResponse | undefined;
}) {
  if (error !== undefined) return <InlineResult tone="error">{error}</InlineResult>;
  if (result === undefined) return null;
  return (
    <InlineResult requestId={result.requestId}>
      {result.connection.displayName} bağlandı.
      {result.webhookSetup === undefined ? null : (
        <>
          {" "}
          Webhook adresi: <code>{result.webhookSetup.endpoint}</code>
        </>
      )}
    </InlineResult>
  );
}

function ConnectionRow({
  connection,
  projectId,
}: {
  readonly connection: SourceConnection;
  readonly projectId: string;
}) {
  const [status, setStatus] = useState<"idle" | "checking" | "reachable" | "failed">("idle");
  const [error, setError] = useState<string>();
  const test = async () => {
    setStatus("checking");
    setError(undefined);
    try {
      await sourcesApi.test(projectId, connection.id);
      setStatus("reachable");
    } catch (caught) {
      setStatus("failed");
      setError(apiErrorMessage(caught));
    }
  };
  return (
    <article className="source-connection-row">
      <div>
        <strong>{connection.displayName}</strong>
        <p className="muted technical">{connection.repository.fullName}</p>
      </div>
      {status === "checking" ? <StatusBadge status="checking" /> : null}
      {status === "reachable" ? <StatusBadge label="Erişilebilir" status="healthy" /> : null}
      {status === "failed" ? <StatusBadge label="Bağlantı başarısız" status="error" /> : null}
      <Button
        busy={status === "checking"}
        busyLabel="Kontrol ediliyor…"
        onClick={() => {
          void test();
        }}
        variant="secondary"
      >
        <ShieldCheck aria-hidden="true" size={18} /> Bağlantıyı test et
      </Button>
      {error === undefined ? null : <p className="field__error">{error}</p>}
    </article>
  );
}

function progressLabel(phase: UploadProgress["phase"]): ReactNode {
  if (phase === "rehashing") return "Devam noktası doğrulanıyor";
  if (phase === "finalizing") return "SHA-256 doğrulanıyor ve tarama başlatılıyor";
  return "Karantinaya yükleniyor";
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "—";
  if (bytes < 1_024) return `${String(bytes)} B`;
  const units = ["KiB", "MiB", "GiB"] as const;
  let value = bytes / 1_024;
  let unit: string = units[0];
  for (let index = 1; index < units.length && value >= 1_024; index += 1) {
    value /= 1_024;
    unit = units[index] ?? unit;
  }
  return `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(value)} ${unit}`;
}
